import { ApiProblem } from './problem';
import { apiFetch, apiJson, publicApiUrl } from './client';

/**
 * L'identité de l'établissement (D77) : logo, emblème, nom officiel par
 * langue. Lue par tous au démarrage, changée par qui détient
 * institution.brand.
 */
export type BrandingKind = 'logo' | 'emblem';

export interface BrandingFile {
    readonly url: string;
    readonly mime: string | null;
    readonly width: number | null;
    readonly height: number | null;
    readonly size: number | null;
    readonly name: string | null;
}

export interface BrandingView {
    readonly version: number;
    readonly code: string;
    readonly names: { readonly fr: string; readonly en: string | null; readonly ar: string | null };
    readonly logo: BrandingFile | null;
    readonly emblem: BrandingFile | null;
}

/** Ce que le contrôle d'un fichier a trouvé (rien n'est encore enregistré). */
export interface BrandingAnalysis {
    readonly mime: string;
    readonly width: number;
    readonly height: number;
    readonly size: number;
    readonly transparent: boolean | null;
    readonly margin: number | null;
    readonly warnings: readonly ('small' | 'margins' | 'opaque')[];
    readonly trimmed?: { readonly width: number; readonly height: number; readonly size: number; readonly warnings: readonly string[] };
}

export interface BrandingHistoryItem {
    readonly number: number;
    readonly change: 'logo' | 'emblem' | 'names' | 'restore';
    readonly restoredFrom: number | null;
    readonly at: string;
    readonly by: string;
    readonly names: BrandingView['names'];
    readonly logo: string | null;
    readonly emblem: string | null;
    readonly current: boolean;
}

/** Sans jeton : la page de connexion la lit aussi. */
export async function fetchBranding(): Promise<BrandingView> {
    const response = await fetch(publicApiUrl('/api/public/branding'), { headers: { Accept: 'application/json' }, credentials: 'omit' });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return (await response.json()) as BrandingView;
}

export const brandingHistory = async (): Promise<readonly BrandingHistoryItem[]> => (await apiJson<{ readonly items: readonly BrandingHistoryItem[] }>('/api/branding/history')).items;

export const saveBrandingNames = (names: { readonly nameFr: string; readonly nameEn: string; readonly nameAr: string }): Promise<BrandingView> => apiJson<BrandingView>('/api/branding/names', { method: 'POST', body: JSON.stringify(names) });

export const restoreBranding = (number: number): Promise<BrandingView> => apiJson<BrandingView>(`/api/branding/restore/${number}`, { method: 'POST' });

async function sendFile<T>(path: string, file: File): Promise<T> {
    const response = await apiFetch(path, { method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return (await response.json()) as T;
}

const params = (values: Readonly<Record<string, string | null>>): string => {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(values)) {
        if (value !== null) {
            query.set(key, value);
        }
    }

    return query.toString();
};

export const checkBrandingFile = (kind: BrandingKind, file: File, trim: boolean): Promise<BrandingAnalysis> => sendFile(`/api/branding/${kind}?${params({ check: '1', trim: trim ? '1' : null })}`, file);

export const uploadBrandingFile = (kind: BrandingKind, file: File, trim: boolean): Promise<BrandingView & { readonly analysis: BrandingAnalysis }> => sendFile(`/api/branding/${kind}?${params({ name: file.name, trim: trim ? '1' : null })}`, file);

/** Le nom dans la langue de l'écran, repli sur le français. */
export function brandingName(view: BrandingView | null, locale: string): string | null {
    if (view === null) {
        return null;
    }

    const name = locale === 'en' ? view.names.en : locale === 'ar' ? view.names.ar : null;

    return name === null || name === '' ? view.names.fr : name;
}
